/**
 * @fileoverview Hook for fetching and caching group names
 */

import { useState, useEffect, useMemo } from 'react'
import { api } from 'helpers'

interface GroupApiResponse {
  id: string
  name: string
  cycle: string
  grade: string
}

interface UseGroupNamesReturn {
  groupNames: Map<string, string>
  isLoading: boolean
  error: string | null
  getGroupName: (groupId: string | null) => string | null
}

const ENDPOINT = '/api/tutors/my_groups'

/**
 * Fetches tutor's groups and provides a lookup map for group names.
 * Caches results to avoid redundant API calls.
 */
export const useGroupNames = (): UseGroupNamesReturn => {
  const [groups, setGroups] = useState<GroupApiResponse[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true

    const fetchGroups = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const response = await api.get<GroupApiResponse[]>(ENDPOINT)

        if (isMounted) {
          setGroups(response.data)
        }
      } catch (err) {
        if (isMounted) {
          setError('Failed to load groups')
          console.error('useGroupNames error:', err)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    fetchGroups()

    return () => {
      isMounted = false
    }
  }, [])

  const groupNames = useMemo((): Map<string, string> => {
    const map = new Map<string, string>()

    groups.forEach(group => {
      const displayName = group.grade
        ? `${group.name} - ${group.grade}`
        : group.cycle
          ? `${group.name} - ${group.cycle}`
          : group.name

      map.set(group.id, displayName)
    })

    return map
  }, [groups])

  const getGroupName = useMemo(() => {
    return (groupId: string | null): string | null => {
      if (!groupId) return null
      return groupNames.get(groupId) || null
    }
  }, [groupNames])

  return {
    groupNames,
    isLoading,
    error,
    getGroupName,
  }
}
